import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';

import {
  JobSimilarPositionsType,
  ModalWrapperCloseIcon,
  PrivateModeProtectionIcon,
  formatDateIn,
  useModal,
  useResponsiveBreakPoints,
  getCurrencySymbol,
} from '@restworld/utils-common';

import { LinkBase, Button, BaseModal } from '../..';
import {
  Alert,
  Heading,
  SkeletonLoading,
  Text,
  Button as DsButton,
  Badge as DsBadge,
  type IBadgeProps,
  type IconComponentType,
} from '@restworld/ui-ds-base';
import {
  ArrowUturnLeftIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  DocumentTextIcon,
  FaceFrownIcon,
  FireIcon,
  HeartIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { JobApplicationStatusKey, StatusSentiment } from '@restworld/utility-types';
import { cn } from '@restworld/utils-common';

export type JobApplicationCardProps = Pick<
  JobSimilarPositionsType,
  | 'redirectUrl'
  | 'restaurantName'
  | 'city'
  | 'jobTitle'
  | 'occupation'
  | 'salaryMin'
  | 'salaryMax'
  | 'salaryCurrency'
  | 'jobType'
  | 'publishedAt'
> & {
  isLoading?: boolean;
  id?: string;
  sentiment?: StatusSentiment;
  sentimentLabel?: string;
  sentimentDescription?: string;
  restaurantId?: string;
  statusKey?: JobApplicationStatusKey;
  onRedirect?: () => void;
  showRestaurantDetails?: boolean;
  showSentimentInfo?: boolean;
  discardedByAI?: boolean;
  discardedBy?: string;
  redirectWorkerToNewTab?: boolean;
  /**
   * The pre-interview chat with Serena for this application (Worker AI Chat, doc 02 D7).
   * Presentation only: the card takes the state already decided and renders the badge in
   * place of the application status, plus the button to the chat while it is `pending`.
   * Nothing here derives the state or the countdown: the page does, from
   * `assessment_channel` and `assessment_status` on the application. The mock that used to
   * stand in for them is gone as of this same change.
   */
  preInterview?: PreInterviewOnCard;
};
export function JobApplicationCard({
  restaurantName,
  city,
  jobTitle,
  occupation,
  salaryMin,
  salaryMax,
  salaryCurrency,
  jobType,
  publishedAt,
  redirectUrl,
  isLoading,
  id,
  sentimentLabel,
  sentimentDescription,
  restaurantId,
  statusKey,
  onRedirect,
  showRestaurantDetails,
  sentiment,
  showSentimentInfo = true,
  discardedByAI,
  discardedBy,
  redirectWorkerToNewTab,
  preInterview,
}: JobApplicationCardProps) {
  // The label next to this date is translated; the date itself was not, so an
  // English reader was shown "Sent on 10 ago 2026". The language comes from
  // i18n rather than from the Next router on purpose: this is a design-system
  // component, and useRouter() *throws* where no router is mounted, which took
  // the card down in Storybook.
  const { t, i18n } = useTranslation();
  const locale = i18n?.language;
  const currencySymbol = useMemo(() => getCurrencySymbol(salaryCurrency), [salaryCurrency]);
  const hideRestaurantDetails =
    !showRestaurantDetails && !(sentiment === 'positive' && statusKey !== 'worker_jp_interested');
  // Why the application ended where it did, or that a spontaneous one cannot be tracked.
  const hasNote = showSentimentInfo && !!(sentimentDescription || restaurantId);
  return isLoading ? (
    <SkeletonLoading variant="job-positions-card" />
  ) : (
    <CardWrapper id={id}>
      <ContentWrapper>
        <RestaurantHeaderDetails>
          <RestaurantHeader>
            {/* The position first, the venue and the town under it: what the worker is
                looking for in a list of their own applications is which job this is. */}
            {!!(!restaurantId ? jobTitle : restaurantName) && (
              <Heading as="span" size={5} weight="semibold">
                {!restaurantId ? jobTitle : restaurantName ?? t('common:name_hidden')}
              </Heading>
            )}
            <RestaurantInfo>
              {!hideRestaurantDetails && (
                <TitleWrapper
                  {...{
                    title: restaurantName,
                    icon: <BuildingOfficeIcon className="w-5 h-5 text-black" />,
                    hidden: restaurantId ? true : false,
                  }}
                />
              )}
              {!hideRestaurantDetails && (
                <TitleWrapper {...{ title: city, icon: <MapPinIcon className="w-5 h-5 text-black" /> }} />
              )}
              {hideRestaurantDetails && (
                <ParagraphLabelWithIcon icon={<PrivateModeProtectionIcon />} label={t('common:hidden_place')} />
              )}
              {publishedAt && (
                <TitleWrapper
                  {...{
                    title: `${t('common:sent_on')} ${formatDateIn(publishedAt, locale)}`,
                    icon: <CalendarIcon className="w-5 h-5 text-black" />,
                  }}
                />
              )}
            </RestaurantInfo>
          </RestaurantHeader>
          {preInterview ? (
            <PreInterviewBadge {...preInterview} />
          ) : (
            sentimentLabel && (
              <ApplicationStatusBadge
                discardedByAI={discardedByAI}
                discardedBy={discardedBy}
                statusKey={statusKey}
                restaurantId={restaurantId}
              >
                {sentimentLabel}
              </ApplicationStatusBadge>
            )
          )}
        </RestaurantHeaderDetails>
        {!restaurantId && (
          <JobInfoWrapper>
            {occupation ? <Badge>{occupation}</Badge> : null}
            {salaryMin && salaryMax ? (
              <Badge>
                {currencySymbol}
                {salaryMin}-{salaryMax}
              </Badge>
            ) : null}
            {jobType ? <Badge>{jobType}</Badge> : null}
          </JobInfoWrapper>
        )}
        {/* While the pre-interview is waiting, the card carries one action and no second
            road: "Apri dettagli" comes back as soon as the chat is done or expired. */}
        {hasNote && (
          <MoreInformation
            {...{
              restaurantName,
              city,
              jobTitle,
              sentimentDescription,
              occupation,
              salaryMax,
              salaryMin,
              jobType,
              sentimentLabel,
              restaurantId,
              statusKey,
              discardedBy,
            }}
          />
        )}
        {redirectUrl && preInterview?.status !== 'pending' && (
          <PublishLocationWrapper restaurantId={restaurantId}>
            {redirectUrl && (
              // The design-system ghost button, the borderless one: same neutral type as the
              // rest of the card, and a real hit area instead of a bare purple link.
              <LinkBase
                onLinkClick={() => {
                  if (onRedirect) onRedirect();
                }}
                link={redirectUrl}
                newTab={redirectWorkerToNewTab}
                twStyle={'w-full lg:w-auto'}
              >
                {/* Full width on a phone, like the pre-interview button, and back to its own
                    width from lg, where it sits on the right of the card. */}
                <DsButton variant="ghost" className="w-full lg:w-auto">
                  {t('common:open_details')}
                </DsButton>
              </LinkBase>
            )}
          </PublishLocationWrapper>
        )}
        {preInterview?.status === 'pending' && preInterview.href && (
          // Full width on a phone, right-aligned from lg. It carries the rule that the
          // footer would have drawn, since the footer is not rendered in this state.
          <div className="flex w-full lg:justify-end pt-4 border-t border-gray-300">
            <LinkBase link={preInterview.href} newTab={false} twStyle="w-full lg:w-auto">
              {/* Full width on a phone; from lg it keeps a 320px floor, so the one action of
                  the card does not shrink to the length of its label. */}
              <DsButton variant="primary" fullwidth className="lg:min-w-[320px]">
                {t('job-application-page:complete_pre_interview')}
              </DsButton>
            </LinkBase>
          </div>
        )}
      </ContentWrapper>
    </CardWrapper>
  );
}
type ApplicationStatusBadgeProps = Partial<
  Pick<JobApplicationCardProps, 'statusKey' | 'restaurantId' | 'discardedByAI' | 'discardedBy'>
> & {
  children?: React.ReactNode;
};

type BadgeConfig = { variant: IBadgeProps['variant']; labelKey: string; Icon?: IconComponentType };

/**
 * One badge per state, on the design-system `Badge` and its tokens.
 *
 * The colour says what kind of moment this is, the label and the icon say which one:
 * `restworld` for everything still open, `warning` for the only state that asks the worker
 * to act, `neutral` for the outcomes with nothing left to do, `success` for the hiring,
 * `disabled` for what the worker closed themselves. No red anywhere: an application that
 * ends is not an error.
 *
 * Two keys deliberately render nothing. `worker_jp_proposed` is never a card (it lives in
 * the recommended carousel), and `worker_jp_not_selected` has no label of its own yet.
 */
function getStatusBadgeConfig(statusKey: JobApplicationStatusKey): BadgeConfig {
  switch (statusKey) {
    case 'worker_jp_interested':
      // Grey, not purple: the application is in, and nothing has happened to it yet. The
      // document rather than a clock, because what happened is that the CV arrived.
      return { variant: 'neutral', labelKey: 'common:application_received', Icon: DocumentTextIcon };
    case 'worker_jp_viewed_by_employer':
    case 'worker_jp_in_shortlist':
      return { variant: 'restworld', labelKey: 'common:application_sent', Icon: PaperAirplaneIcon };
    case 'worker_jp_employer_interested':
      return { variant: 'restworld', labelKey: 'common:employer_interested', Icon: HeartIcon };
    case 'worker_jp_interview_scheduled':
    case 'worker_jp_interview_done':
      return { variant: 'restworld', labelKey: 'common:interview_scheduled', Icon: CalendarIcon };
    case 'worker_jp_stage_scheduled':
    case 'worker_jp_stage':
    case 'worker_jp_stage_done':
      return { variant: 'restworld', labelKey: 'common:trial_period', Icon: FireIcon };
    case 'worker_jp_scheduled_hiring':
    case 'worker_jp_hired':
      return { variant: 'success', labelKey: 'common:hired', Icon: CheckCircleIcon };
    case 'worker_jp_discarded':
      return { variant: 'neutral', labelKey: 'common:not_fit', Icon: XCircleIcon };
    case 'worker_jp_not_interested':
      // The worker said no to a position we proposed, so the label says so.
      return { variant: 'neutral', labelKey: 'common:rejected', Icon: XCircleIcon };
    case 'worker_jp_proposed':
    case 'worker_jp_not_selected':
      return { variant: 'neutral', labelKey: '' };
    default:
      return { variant: 'neutral', labelKey: '' };
  }
}

export const ApplicationStatusBadge = ({
  statusKey,
  restaurantId,
  discardedByAI,
  discardedBy,
}: ApplicationStatusBadgeProps) => {
  const { t } = useTranslation();
  const isWithdrawnByWorker = statusKey === 'worker_jp_discarded' && discardedBy === 'worker';
  const { variant, label, Icon } = useMemo<{
    variant: IBadgeProps['variant'];
    label: string;
    Icon?: IconComponentType;
  }>(() => {
    // Not a state but a kind of application: the worker wrote to the venue, not to a
    // position, so nothing downstream will ever move it.
    if (restaurantId) {
      return {
        variant: 'restworld',
        label: t('common:spontaneous_application'),
        Icon: ClipboardDocumentIcon,
      };
    }
    if (!statusKey) return { variant: 'neutral', label: '' };
    // Both of these override the state: the worker pulling out, and the AI having said no.
    if (isWithdrawnByWorker) {
      return {
        variant: 'disabled',
        label: t('job-application-page:withdrawn_application'),
        Icon: ArrowUturnLeftIcon,
      };
    }
    if (discardedByAI) {
      return { variant: 'neutral', label: t('job-application-page:profile_not_selected'), Icon: FaceFrownIcon };
    }
    const config = getStatusBadgeConfig(statusKey);
    return { variant: config.variant, label: config.labelKey ? t(config.labelKey) : '', Icon: config.Icon };
  }, [discardedByAI, isWithdrawnByWorker, restaurantId, statusKey, t]);

  if (!label) return null;

  return (
    <CardBadge variant={variant} icon={Icon}>
      {label}
    </CardBadge>
  );
};

/**
 * The design-system badge, with one local departure: its fixed 24px single line becomes a
 * minimum, so a long label wraps instead of being cut on a phone. Everything else is the
 * design system's, type and 16px icon included, so these badges match the ones elsewhere in
 * the app.
 *
 * Alignment stays `items-center` from the base — an earlier `items-start` here pinned the
 * icon to the top of the badge, which is what "the icon wobbles" looked like.
 */
const CardBadge = ({ className, ...props }: IBadgeProps) => (
  <DsBadge className={cn('h-auto min-h-6 whitespace-normal text-left', className)} {...props} />
);

export type PreInterviewStatus = 'pending' | 'completed' | 'expired';

export type PreInterviewOnCard = {
  status: PreInterviewStatus;
  /** Chat page for this assessment. Only read while `pending`. */
  href?: string;
};

const PRE_INTERVIEW_BADGE: Record<PreInterviewStatus, BadgeConfig> = {
  // The only state on the whole card that asks the worker to do something, and the only
  // orange one.
  pending: { variant: 'warning', labelKey: 'job-application-page:pre_interview_to_complete', Icon: ClockIcon },
  completed: {
    variant: 'restworld',
    labelKey: 'job-application-page:pre_interview_completed',
    Icon: CheckCircleIcon,
  },
  // Not a rejection: the application is evaluated anyway, with whatever the worker answered.
  expired: { variant: 'neutral', labelKey: 'job-application-page:pre_interview_expired', Icon: ClockIcon },
};

/**
 * The state of the interview, drawn the same way wherever it appears: the card in the list
 * and the "Intervista" step of the detail timeline share this one badge, so the two places
 * are recognisable as the same thing at a glance.
 */
export const PreInterviewBadge = ({ status }: PreInterviewOnCard) => {
  const { t } = useTranslation();
  const { variant, labelKey, Icon } = PRE_INTERVIEW_BADGE[status];
  return (
    <CardBadge variant={variant} icon={Icon}>
      {t(labelKey)}
    </CardBadge>
  );
};

export default JobApplicationCard;
const CardWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col w-full border lg:border-gray-200 border-gray-300 bg-white lg:rounded-[20px] rounded-3.75 items-start overflow-hidden',
      className
    )}
    {...props}
  />
);

const ContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  // One 16px rhythm for every block in the card: the blocks used to carry their own margins,
  // and a card without the info pills (a spontaneous application) left the note glued to the
  // header above it.
  <div className={cn('flex flex-col w-full items-start gap-4 lg:p-6 p-4', className)} {...props} />
);

const RestaurantHeaderDetails = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex lg:flex-row flex-col-reverse w-full lg:justify-between justify-start items-start lg:gap-0 gap-4',
      className
    )}
    {...props}
  />
);

const RestaurantHeader = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-start gap-2', className)} {...props} />
);

const RestaurantInfo = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  // Three items now (venue, town, date), so they wrap instead of overflowing a phone.
  <div className={cn('flex flex-row flex-wrap items-start gap-x-5 gap-y-3 w-fit', className)} {...props} />
);

const PublishLocationWrapper = ({
  restaurantId,
  className,
  children,
  ...props
}: Pick<JobApplicationCardProps, 'restaurantId'> & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // One child, the action: centred on a phone, on the right from lg.
      'flex flex-row items-center justify-center lg:justify-end w-full pt-4 border-t border-gray-300',
      restaurantId && 'mt-4',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

/**
 * The role, pay and contract pills. The same design-system badge as the state above them, in
 * `neutral`: they are facts about the position, and the colour is not carrying meaning here.
 * They were a FlowBite badge on `primary-50` with `gray-600` text, neither of which is a token.
 */
const Badge = ({ className, ...props }: IBadgeProps) => (
  <CardBadge variant="neutral" className={className} {...props} />
);

const JobInfoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  // Role, pay and contract wrap like the line above them: three pills and a long role name
  // ran past the edge of the card on a phone.
  <div
    className={cn('flex flex-row flex-wrap gap-2 items-center justify-start w-fit max-w-full', className)}
    {...props}
  />
);

type TitleWrapperProps = { title?: string | React.ReactNode; icon: React.ReactNode; hidden?: boolean };

const TitleWrapper = ({ title, icon, hidden }: TitleWrapperProps) => {
  const { t } = useTranslation();
  if (hidden) return null;
  return (
    <div className="flex flex-row items-center gap-2">
      {icon}
      <Text size={2} color="black" weight="normal">
        {title ?? t('common:name_hidden')}
      </Text>
    </div>
  );
};

export const ParagraphLabelWithIcon = ({
  icon,
  label,
  maxChars,
}: {
  icon: React.ReactNode;
  label: string;
  maxChars?: number;
}) => (
  <div className="flex flex-row items-center gap-2">
    {icon}
    <Text size={2} color="black" weight="normal" maxChars={maxChars}>
      {label}
    </Text>
  </div>
);

const MoreInformation = ({
  restaurantName,
  city,
  jobTitle,
  statusKey,
  sentimentLabel,
  occupation,
  salaryMax,
  salaryMin,
  jobType,
  sentimentDescription,
  restaurantId,
  discardedBy,
}: Pick<
  JobApplicationCardProps,
  | 'restaurantName'
  | 'city'
  | 'jobTitle'
  | 'occupation'
  | 'salaryMax'
  | 'salaryMin'
  | 'jobType'
  | 'sentimentDescription'
  | 'sentimentLabel'
  | 'restaurantId'
  | 'statusKey'
  | 'discardedBy'
>) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsiveBreakPoints();
  const contentLength = sentimentDescription?.length ?? 0;
  const limitContentLength = isDesktop ? 300 : 89;
  const hasCrossedLimit = contentLength > limitContentLength;
  const content = hasCrossedLimit
    ? (sentimentDescription?.slice(0, limitContentLength) || '') + '..'
    : sentimentDescription;
  return (
    // The design-system alert, so this note looks the same as every other one in the app.
    // It used to be a purple band welded to the bottom edge of the card.
    <Alert
      variant="restworld"
      // `lg:self-start` because the alert's own base carries `self-stretch`, which would keep
      // it the full width of the card on desktop whatever the width class says.
      className="w-full lg:w-auto lg:self-start items-start"
      textClassName="!text-sm !leading-[21px]"
      text={
        <span className="flex flex-col items-start gap-1">
          {/* No title here: the badge at the top of the card already says "Candidatura
              spontanea", so the note only has to explain what follows from it. */}
          <span className="font-normal">
            {restaurantId ? t('job-application-page:applications_not_traceable_by_restworld') : content}
          </span>
          {hasCrossedLimit && !restaurantId && (
            <ShowMoreInformationModal
              {...{
                sentimentLabel,
                restaurantName,
                city,
                jobTitle,
                sentimentDescription,
                statusKey,
                occupation,
                salaryMax,
                salaryMin,
                jobType,
                restaurantId,
                discardedBy,
              }}
            />
          )}
        </span>
      }
    />
  );
};

const ShowMoreInformationModal = ({
  restaurantName,
  city,
  jobTitle,
  sentimentLabel,
  occupation,
  salaryMax,
  salaryMin,
  jobType,
  sentimentDescription,
  restaurantId,
  statusKey,
  discardedBy,
}: Pick<
  JobApplicationCardProps,
  | 'restaurantName'
  | 'city'
  | 'jobTitle'
  | 'occupation'
  | 'salaryMax'
  | 'salaryMin'
  | 'jobType'
  | 'sentimentDescription'
  | 'sentimentLabel'
  | 'restaurantId'
  | 'statusKey'
  | 'discardedBy'
>) => {
  const { t } = useTranslation();
  const { close, open, show } = useModal();
  return (
    <>
      <div className="flex flex-row items-start justify-start w-full ">
        <Button
          onClick={open}
          title={t('common:show_more')}
          variant="ghost"
          suffixIcon="PlusPurpleSmallIcon"
          fullwidth={false}
          fontColor="primary"
          twStyle={'p-0 !w-fit font-medium'}
        />{' '}
      </div>
      <BaseModal
        {...{
          onClose: close,
          showMobileGestures: true,
          open: show,
          modalStyles: ['w-full md:max-w-[26rem] md:min-w-[22rem]'],
          closeIcon: <ModalWrapperCloseIcon />,
        }}
      >
        <div className="flex flex-row items-start mb-6 w-full">
          <ApplicationStatusBadge statusKey={statusKey} restaurantId={restaurantId} discardedBy={discardedBy}>
            {sentimentLabel}
          </ApplicationStatusBadge>
        </div>
        <div className="flex flex-col gap-4 items-start w-full">
          <RestaurantHeader>
            <RestaurantInfo>
              <TitleWrapper
                {...{ title: restaurantName, icon: <BuildingOfficeIcon className="w-5 h-5 text-black" /> }}
              />
              <TitleWrapper {...{ title: city, icon: <MapPinIcon className="w-5 h-5 text-black" /> }} />
            </RestaurantInfo>
            <Heading as="span" size={5} weight="semibold">
              {jobTitle}
            </Heading>
          </RestaurantHeader>
        </div>
        <div className="flex flex-row gap-2 items-center justify-start w-fit my-6">
          {occupation ? <Badge>{occupation}</Badge> : null}
          {salaryMin && salaryMax ? (
            <Badge>
              €{salaryMin}-{salaryMax}
            </Badge>
          ) : null}
          {jobType ? <Badge>{jobType}</Badge> : null}
        </div>
        <Text size={2} color="muted" weight="normal" className="mb-6">
          {sentimentDescription}
        </Text>
        <Button fullwidth title={t('common:close')} onClick={close} />
      </BaseModal>
    </>
  );
};
